import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { countryLabel, countryOptions } from "#shared/countries.ts";
import { cn } from "@web/lib/utils.ts";

const INPUT =
  "h-8 w-full min-w-0 rounded-md border border-line bg-panel px-2 text-sm text-text placeholder:text-faint focus-visible:outline-none";

/**
 * Single-select country picker: type to filter ISO English names, pick one code
 * (or Other). Clears to null via the empty row.
 */
export function CountryCombobox({
  value,
  disabled,
  "aria-label": ariaLabel,
  onCommit,
}: {
  value: string | undefined;
  disabled: boolean;
  "aria-label": string;
  onCommit: (next: string | null) => void;
}) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const options = useMemo(() => countryOptions(), []);
  const selectedLabel = value ? countryLabel(value) : "";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    setHighlight(0);
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function pick(code: string | null) {
    onCommit(code);
    setOpen(false);
    setQuery("");
  }

  function openMenu() {
    if (disabled) return;
    setQuery("");
    setOpen(true);
  }

  return (
    <div ref={rootRef} className="relative">
      {open ? (
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder="Search countries…"
          value={query}
          className={cn(INPUT, "pr-8 disabled:opacity-50")}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              setOpen(false);
              setQuery("");
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((i) => Math.min(i + 1, filtered.length)); // + empty row
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((i) => Math.max(i - 1, 0));
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              // Index 0 = clear; 1..n = filtered[i-1]
              if (highlight === 0) {
                pick(null);
                return;
              }
              const opt = filtered[highlight - 1];
              if (opt) pick(opt.code);
            }
          }}
        />
      ) : (
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={false}
          className={cn(
            INPUT,
            "flex items-center justify-between gap-2 pr-2 text-left disabled:opacity-50",
            !selectedLabel && "text-faint",
          )}
          onClick={openMenu}
        >
          <span className="min-w-0 truncate">{selectedLabel || "—"}</span>
          <ChevronDown className="size-3.5 shrink-0 text-faint" aria-hidden />
        </button>
      )}

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-line bg-panel-elevated py-1 shadow-md"
        >
          <li role="option" aria-selected={!value}>
            <button
              type="button"
              className={cn(
                "flex w-full px-2 py-1.5 text-left text-sm text-muted hover:bg-row-hover hover:text-text",
                highlight === 0 && "bg-row-hover text-text",
              )}
              onMouseEnter={() => setHighlight(0)}
              onClick={() => pick(null)}
            >
              —
            </button>
          </li>
          {filtered.length === 0 ? (
            <li className="px-2 py-1.5 text-sm text-faint">No matches</li>
          ) : (
            filtered.map((opt, i) => {
              const index = i + 1;
              const selected = value === opt.code;
              return (
                <li key={opt.code} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    className={cn(
                      "flex w-full items-baseline justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-row-hover",
                      (highlight === index || selected) && "bg-row-hover text-text",
                      !selected && highlight !== index && "text-muted",
                    )}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => pick(opt.code)}
                  >
                    <span className="min-w-0 truncate">{opt.name}</span>
                    <span className="shrink-0 text-xs text-faint">{opt.code}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
